import { CartAddEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * "Complete your carry" PDP module.
 *
 * Two picker styles (container's data-picker-style, set in the theme editor):
 *  - inline: every swatch always visible; the + toggles a pending selection.
 *  - expand: rows collapse to "name · $ · colour · matched"; + opens the
 *    picker in place (one row at a time) with FABRIC TABS — only the active
 *    fabric's swatches show — and a full-width confirm that COMMITS the
 *    selection (✓ state).
 *
 * In BOTH styles, committed/pending rows ride the main Add to Cart button:
 * it grows a "+ n · $x" note, and after the theme's own add succeeds this
 * element chains ONE cart/add call for every selected accessory (sections
 * included, so the cart drawer refreshes itself through the standard event).
 *
 * All clicks are DELEGATED from the element root — the theme morphs section
 * DOM on variant/cart updates, and per-node listeners would be lost.
 */

/** Parsed fresh on every use — colour swaps morph new row data into the same
 *  DOM nodes, so caching by element would serve stale entries.
 *  @param {Element} row */
function rowEntries(row) {
  try {
    return JSON.parse(row.querySelector('[data-cyc-data]')?.textContent || '[]');
  } catch (error) {
    console.warn('[complete-your-carry] bad row data', error);
    return [];
  }
}

/** @param {Element} row @param {string|number} index */
function rowEntryAt(row, index) {
  return rowEntries(row).find((entry) => entry.index === Number(index));
}

/** Swap an image with the module's standard fade. */
function fadeSwap(img, src) {
  if (!img || !src || img.src === src) return;
  img.classList.add('cyc-img-loading');
  const done = () => img.classList.remove('cyc-img-loading');
  img.addEventListener('load', done, { once: true });
  img.addEventListener('error', done, { once: true });
  img.src = src;
}

class CompleteYourCarry extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;

    // Slash server-rendered prices on rows the bundle discount applies to
    // (Liquid flags eligibility; the percent lives on this container).
    this.#applyInitialDiscounts();

    // QA-only: on UNPUBLISHED themes, flag the cart so the discount
    // function's preview guard applies the real discount to this cart
    // (visible in cart AND checkout during QA). Never runs on the live
    // theme (role 'main'), so real customers are untouched.
    if (window.Shopify?.theme?.role !== 'main' && !sessionStorage.getItem('cycPreviewFlagged')) {
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { __cyc_preview: '1' } }),
      })
        .then(() => sessionStorage.setItem('cycPreviewFlagged', '1'))
        .catch(() => {});
    }

    this.addEventListener('click', this.#onClick, { signal });

    // CartAddEvent shares its event name with cartUpdate; filtered in handler.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Colour swaps on combined listings don't reload the page — the theme
    // fetches the sibling product's HTML and each component patches itself.
    // Morph in the fresh module whenever the swap lands on a new product.
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get pickerStyle() {
    return this.dataset.pickerStyle || 'inline';
  }

  /* ------------- bundle discount (display only) -------------
     The Shopify Function applies the real discount in cart/checkout; the
     module mirrors it so shoppers see honest prices before they commit.
     Liquid flags each eligible row (data-cyc-disc, mirroring the Function's
     rules); the percent is a container setting that must match the discount
     in admin. Percent 0 = display off. */

  get #discountPercent() {
    return Number(this.dataset.discountPercent) || 0;
  }

  /** @param {Element} row */
  #rowDiscounted(row) {
    return this.#discountPercent > 0 && row.dataset.cycDisc === '1';
  }

  /** @param {number} cents */
  #discCents(cents) {
    return Math.round((cents * (100 - this.#discountPercent)) / 100);
  }

  /** Paint a row's price line (and confirm-button price). A discounted row
   *  shows "final ~~original~~" — the slash REPLACES any compare-at (never
   *  three prices) — and hides the set-saving note (never two messages). */
  #paintPrice(row, cents, priceText, entry) {
    const price = row.querySelector('[data-cyc-price]');
    const compare = row.querySelector('[data-cyc-compare]');
    const setNote = row.querySelector('[data-cyc-setnote]');
    const confirmPrice = row.querySelector('[data-cyc-confirm-price]');

    const disc = this.#rowDiscounted(row) && Number.isFinite(cents);
    const shown = disc ? this.#formatMoney(this.#discCents(cents)) : priceText;

    if (price) price.textContent = shown;
    if (compare) {
      if (disc) {
        compare.textContent = priceText;
        compare.hidden = false;
      } else if (entry && entry.hasCompare) {
        compare.textContent = entry.compare;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
    if (setNote) setNote.hidden = disc;
    if (confirmPrice) confirmPrice.textContent = shown;
  }

  /** Server HTML renders full prices (child blocks can't read this
   *  container's percent) — correct the flagged rows once per render.
   *  Re-run after every morph: it replaces rows with fresh server HTML. */
  #applyInitialDiscounts() {
    if (!(this.#discountPercent > 0)) return;
    for (const row of this.querySelectorAll('[data-cyc-item]')) {
      if (!this.#rowDiscounted(row)) continue;
      const price = row.querySelector('[data-cyc-price]');
      const priceText = price ? price.textContent : '';
      if (row.hasAttribute('data-cyc-mix')) {
        this.#paintPrice(row, Number(row.dataset.bundleCents), priceText, null);
        continue;
      }
      const entry = rowEntryAt(row, row.dataset.selectedIndex);
      const cents = entry ? entry.cents : Number(row.dataset.rowCents);
      this.#paintPrice(row, cents, entry ? entry.price : priceText, entry || null);
    }
  }

  /* ---------------- delegated clicks ---------------- */

  /** @param {MouseEvent & {target: Element}} event */
  #onClick = (event) => {
    const tab = event.target.closest('[data-cyc-fab-tab]');
    if (tab) return this.#onFabricTab(tab);

    const confirm = event.target.closest('[data-cyc-confirm]');
    if (confirm) return this.#onConfirm(confirm);

    const mixSwatch = event.target.closest('[data-cyc-mix-sw]');
    if (mixSwatch) return this.#onMixSwatch(mixSwatch);

    const swatch = event.target.closest('[data-cyc-sw]');
    if (swatch) return this.#onSwatch(swatch);

    const toggle = event.target.closest('[data-cyc-toggle]');
    if (toggle) return this.#onToggle(toggle);
  };

  /** @param {Element} toggle */
  #onToggle(toggle) {
    const row = toggle.closest('[data-cyc-item]');
    if (!row) return;

    // Inline style, and any row with nothing to choose (single colour/size —
    // no panel exists), commit straight away: + includes it, ✓ removes it.
    if (this.pickerStyle === 'inline' || row.hasAttribute('data-cyc-single')) {
      const pressed = toggle.getAttribute('aria-pressed') === 'true';
      toggle.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      this.#updateAtcNote();
      return;
    }

    // expand style — a committed row's ✓ tap deselects it
    if (toggle.getAttribute('aria-pressed') === 'true') {
      toggle.setAttribute('aria-pressed', 'false');
      row.classList.remove('cyc-item--open');
      this.#updateAtcNote();
      return;
    }

    // + opens the picker, one row at a time; the + rotates to × via CSS
    const wasOpen = row.classList.contains('cyc-item--open');
    this.querySelectorAll('.cyc-item--open').forEach((other) => other.classList.remove('cyc-item--open'));
    if (!wasOpen) row.classList.add('cyc-item--open');
    toggle.setAttribute(
      'aria-label',
      wasOpen ? this.dataset.i18nChooseOptions || 'Choose options' : this.dataset.i18nCloseOptions || 'Close options'
    );
  }

  /** Fabric tab: show only that fabric's swatch group + refresh the hint. */
  #onFabricTab(tab) {
    const row = tab.closest('[data-cyc-item]');
    const fabric = tab.dataset.fabric;
    if (!row || !fabric) return;

    row.querySelectorAll('[data-cyc-fab-tab]').forEach((other) => {
      other.setAttribute('aria-selected', String(other === tab));
    });
    row.querySelectorAll('[data-cyc-fab]').forEach((group) => {
      group.classList.toggle('cyc-fab--active', group.dataset.fabric === fabric);
    });
  }


  /** Confirm = commit this row's selection (✓); items are added together by
   *  the main Add to Cart button, which shows the running "+ n · $x" note. */
  #onConfirm(confirmButton) {
    const row = confirmButton.closest('[data-cyc-item]');
    if (!row || confirmButton.disabled) return;

    if (row.hasAttribute('data-cyc-mix') && row.dataset.bundleAvailable !== 'true') return;
    if (!row.hasAttribute('data-cyc-mix')) {
      const entry = rowEntryAt(row, row.dataset.selectedIndex);
      if (!entry || !entry.available) return;
    }

    row.querySelector('[data-cyc-toggle]')?.setAttribute('aria-pressed', 'true');
    row.classList.remove('cyc-item--open');
    this.#updateAtcNote();
  }

  /** @param {Element} swatch */
  #onSwatch(swatch) {
    const row = swatch.closest('[data-cyc-item]');
    if (!row) return;

    const entry = rowEntryAt(row, swatch.dataset.index);
    if (!entry || !entry.available) return;

    row.dataset.selectedIndex = String(entry.index);
    row.querySelectorAll('[data-cyc-sw]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === swatch));
    });

    // bundle rows can carry a fanned per-component thumb (one layer per cube)
    const stack = row.querySelector('[data-cyc-imgstack]');
    if (stack && entry.imgs) {
      stack.querySelectorAll('img').forEach((tile, i) => fadeSwap(tile, entry.imgs[i]));
    } else {
      fadeSwap(row.querySelector('[data-cyc-img]'), entry.img);
    }

    this.#paintPrice(row, entry.cents, entry.price, entry);

    const label = row.querySelector('[data-cyc-variant]');
    if (label) label.textContent = ' · ' + entry.name;

    // the badge belongs to the matched colour — hide it elsewhere, bring it
    // back when the shopper returns to the match
    const chip = row.querySelector('[data-cyc-matched-chip]');
    if (chip) chip.hidden = entry.index !== Number(row.dataset.matchedIndex);

    this.#updateAtcNote();
  }

  /** Mix & match bundle rows: one dot group per component, combination
   *  resolved from the bundle's variant list embedded in the row. */
  #onMixSwatch(swatch) {
    if (swatch.getAttribute('aria-disabled') === 'true') return;
    const row = swatch.closest('[data-cyc-item]');
    if (!row) return;

    let data;
    try {
      data = JSON.parse(row.querySelector('[data-cyc-bundle-json]')?.textContent || 'null');
    } catch (error) {
      console.warn('[complete-your-carry] bad bundle data', error);
      return;
    }
    if (!data) return;

    const group = swatch.closest('[data-cyc-mix-opt]');
    group?.querySelectorAll('[data-cyc-mix-sw]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === swatch));
    });

    const picks = [...row.querySelectorAll('[data-cyc-mix-opt]')].map(
      (opt) => opt.querySelector('[data-cyc-mix-sw][aria-pressed="true"]')?.dataset.val || ''
    );
    const variant = data.variants.find((entry) => entry.t === picks.join(data.sep));

    const label = row.querySelector('[data-cyc-variant]');
    const toggle = row.querySelector('[data-cyc-toggle]');
    const mixConfirm = row.querySelector('[data-cyc-confirm]');

    const chip = row.querySelector('[data-cyc-matched-chip]');
    if (variant && variant.a) {
      row.dataset.bundleVariantId = String(variant.id);
      row.dataset.bundleCents = String(variant.c);
      row.dataset.bundleAvailable = 'true';
      this.#paintPrice(row, Number(variant.c), variant.p, null);
      const uniform = picks.every((pick) => pick === picks[0]);
      if (label) label.textContent = uniform ? ` · ${picks[0]} set` : ' · Custom set';
      if (chip) chip.hidden = !(uniform && picks[0] === row.dataset.matchedColor);
      toggle?.removeAttribute('disabled');
      if (mixConfirm) mixConfirm.disabled = false;
    } else {
      row.dataset.bundleAvailable = 'false';
      if (label) label.textContent = ' · Combination unavailable';
      if (chip) chip.hidden = true;
      toggle?.setAttribute('aria-pressed', 'false');
      if (mixConfirm) mixConfirm.disabled = true;
    }

    // live preview stack: swap the tapped component's tile to its new colour
    const optIndex = group?.dataset.optIndex;
    const tile = row.querySelector(`[data-cyc-stack] img[data-opt-index="${optIndex}"]`);
    fadeSwap(tile, data.compImages?.[Number(optIndex)]?.[swatch.dataset.val]);

    this.#updateAtcNote();
  }

  /* ---------------- selection → main ATC ---------------- */

  /** Rows toggled ✓ (committed/pending), resolved to their selected entry. */
  #selections() {
    const picks = [];
    for (const row of this.querySelectorAll('[data-cyc-item]')) {
      const toggle = row.querySelector('[data-cyc-toggle]');
      if (!toggle || toggle.getAttribute('aria-pressed') !== 'true') continue;

      if (row.hasAttribute('data-cyc-mix')) {
        if (row.dataset.bundleAvailable !== 'true') continue;
        const id = Number(row.dataset.bundleVariantId);
        const cents = Number(row.dataset.bundleCents);
        if (id) picks.push({ id, cents, available: true, disc: this.#rowDiscounted(row) });
        continue;
      }

      const entry = rowEntryAt(row, row.dataset.selectedIndex);
      if (entry && entry.available) picks.push({ ...entry, disc: this.#rowDiscounted(row) });
    }
    return picks;
  }

  /** Does the shop's money format show cents? Derived from a Liquid-rendered
   *  sample of 10.10 — "$10.10"/"10,10 €" → yes, "$10" → no. */
  get #showsCents() {
    return /[.,]\d{2}(?!\d)/.test(this.dataset.moneySample || '');
  }

  /**
   * On a no-decimals shop a computed price can't be shown exactly, so the
   * rounding direction is chosen to never work against the shopper:
   *   'up'   (prices)  — $26.10 shows as $27, they are charged less.
   *   'down' (savings) — $2.90 shows as $2, they save more than promised.
   * Never the reverse: a price shown lower than charged, or a saving shown
   * larger than given, is the shape of complaint (and of an advertised-price
   * problem). Shops that do show cents get the exact figure either way.
   * @param {number} cents
   * @param {'up'|'down'} direction
   */
  #formatMoney(cents, direction = 'up') {
    const code = this.dataset.currencyCode || '';
    const symbol = this.dataset.currencySymbol || '$';
    const zeroDecimal = ['JPY', 'KRW', 'TWD', 'VND'].includes(code);
    if (zeroDecimal) return symbol + String(Math.round(cents));
    if (!this.#showsCents) {
      const units = cents / 100;
      return symbol + String(direction === 'down' ? Math.floor(units) : Math.ceil(units));
    }
    // Exact, with ".00" trimmed — mirroring the money_without_trailing_zeros
    // filter the theme now uses, so a round price reads "$29" here exactly as
    // it does everywhere else, while $26.10 keeps its cents.
    return symbol + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /** Selection summary INSIDE the module (research: Amazon/Apple/Bellroy all
   *  keep the main buy button untouched and put bundle totals in the widget).
   *  "2 accessories selected · +$53 · added with your bag" */
  #updateAtcNote() {
    const summary = this.querySelector('[data-cyc-summary]');
    if (!summary) return;
    const picks = this.#selections();
    if (!picks.length) {
      summary.hidden = true;
      summary.textContent = '';
      return;
    }
    const original = picks.reduce((sum, entry) => sum + entry.cents, 0);
    const total = picks.reduce(
      (sum, entry) => sum + (entry.disc ? this.#discCents(entry.cents) : entry.cents),
      0
    );
    // localised via data-i18n-* on the element (see complete-your-carry.liquid)
    const selectedTpl =
      (picks.length === 1 ? this.dataset.i18nSelectedOne : this.dataset.i18nSelectedOther) ||
      (picks.length === 1 ? '__N__ accessory selected' : '__N__ accessories selected');
    const suffix = this.dataset.summarySuffix || 'added with your order';

    // Discounted: "2 accessories selected · +$43.20 ~~$48~~ · you save $4.80 · …"
    // (built as DOM nodes — the suffix is editor text and must stay text)
    summary.textContent = '';
    summary.append(`${selectedTpl.replace('__N__', String(picks.length))} · +${this.#formatMoney(total)}`);
    if (total < original) {
      const struck = document.createElement('s');
      struck.textContent = this.#formatMoney(original);
      // saving rounds DOWN so it is never overstated
      const youSave = (this.dataset.i18nYouSave || 'you save __AMT__').replace(
        '__AMT__',
        this.#formatMoney(original - total, 'down')
      );
      summary.append(' ', struck, ` · ${youSave}`);
    }
    summary.append(` · ${suffix}`);
    summary.hidden = false;
  }

  /** @param {Event & {detail?: any}} event */
  #onVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;

    // Same-product variant changes (e.g. a size) keep the shopper's toggles —
    // but the theme's own section morph may still re-render our rows with
    // server (full-price) HTML, wiping the slashed prices. Repaint once the
    // morph settles; a second, delayed pass covers slow patches. Both are
    // idempotent.
    if (!data.newProduct) {
      setTimeout(() => {
        this.#applyInitialDiscounts();
        this.#updateAtcNote();
      }, 0);
      setTimeout(() => this.#applyInitialDiscounts(), 250);
      return;
    }

    const fresh = data.html.querySelector('complete-your-carry');
    if (fresh) {
      morph(this, fresh);
      // morphed rows arrive with server-rendered (full) prices
      this.#applyInitialDiscounts();
    }

    setTimeout(() => this.#updateAtcNote(), 0);
  };

  /** After the theme's own successful add, chain one add for the selection. */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;
    if (String(data.productId) !== String(this.dataset.productId)) return;
    if (this.#busy) return;

    const picks = this.#selections();
    if (!picks.length) return;

    this.#busy = true;

    addItemsToCart(picks.map((entry) => ({ id: entry.id, quantity: 1 })))
      .then((response) => {
        if (!response) return;

        this.querySelectorAll('[data-cyc-toggle][aria-pressed="true"]').forEach((toggle) => {
          toggle.setAttribute('aria-pressed', 'false');
        });
        this.#updateAtcNote();

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'complete-your-carry',
            itemCount: picks.length,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .finally(() => {
        this.#busy = false;
      });
  };
}

if (!customElements.get('complete-your-carry')) {
  customElements.define('complete-your-carry', CompleteYourCarry);
}

/**
 * POST items to cart/add.js with the cart sections bundled in.
 * Resolves to the parsed response on success, null on failure (logged).
 * @param {Array<{id: number, quantity: number}>} items
 */
function addItemsToCart(items) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((item) => item.dataset.sectionId)
    .filter(Boolean);

  return fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        console.warn('[complete-your-carry] add failed:', response.description || response.message);
        return null;
      }
      return response;
    })
    .catch((error) => {
      console.error('[complete-your-carry]', error);
      return null;
    });
}

/**
 * Cart drawer upsell rail (snippets/cart-upsell-rail.liquid).
 * One-tap add; the drawer re-renders itself through the standard event, which
 * also re-runs the rail's own Liquid (items already in the cart drop out).
 * Delegated clicks: the drawer morph can replace the rail's children.
 */
class CartUpsellRail extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {MouseEvent & {target: Element}} event */
  #onClick = (event) => {
    const button = event.target.closest('[data-rail-add]');
    if (!button || button.disabled) return;
    button.disabled = true;

    addItemsToCart([{ id: Number(button.dataset.railAdd), quantity: 1 }]).then((response) => {
      if (!response) {
        button.disabled = false;
        return;
      }
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'cart-upsell-rail',
          itemCount: 1,
          sections: response.sections,
        })
      );
      // no re-enable: the drawer morph replaces this rail's contents
    });
  };
}

if (!customElements.get('cart-upsell-rail')) {
  customElements.define('cart-upsell-rail', CartUpsellRail);
}
